import { createSlice } from '@reduxjs/toolkit';
import zipObject from 'lodash/zipObject';
import { getTranslation } from '../providers/TranslationProvider';

// A mapping of $routeProvider routes' resolve values
const initialRouterState = {
  backPath: undefined,
  bodyClasses: '',
  eventPath: '',
  hasMetadataPageTitle: false,
  pageContext: '',
  pageName: '',
  search: {},
  showBack: false,
  showLogo: false,
  showProfileMenu: false,
  showNav: false,
  titleKey: '',
};

// $routeScope.nav values related to routes and WidgetNav
const initialNavState = {
  navVenueId: undefined,
  navVenueName: '',
  navTitle: '',
  navSubTitle: '',
};

// $rootScope.metadata values related to routes
const titleDefault = getTranslation({ translationKey: 'titles.default' });
const initialMetadataState = {
  metadataPageTitle: '',
  metadataTitle: titleDefault,
  pageName: '',
  bodyClasses: '',
};

// Metadata related to booking, captured from intial search params
const initialBookingMetadataState = {
  affId: undefined,
  offerId: undefined,
  returnUrl: undefined,
  sourceId: undefined,
  utmSource: undefined,
  partnerId: undefined,
  isBookingMetadataInited: false,
};

// Union of the above plus value from $rootScope.logoutRedirect
const initialState = {
  ...initialRouterState,
  ...initialNavState,
  ...initialMetadataState,
  ...initialBookingMetadataState,
  logoutRedirect: undefined,
  mobileFirstLogin: 0,
  previewMode: false,
};

const resyRouterSlice = createSlice({
  name: 'resyRouter',
  initialState,
  reducers: {
    setLogoutRedirect: (state, { payload }) => {
      state.logoutRedirect = payload;
    },
    setMetadataPageTitle: (state, { payload }) => {
      state.metadataPageTitle = payload;
    },
    setMobileFirstLogin: (state, { payload }) => {
      state.mobileFirstLogin = payload ? 1 : 0;
    },
    setPreviewMode: (state, { payload }) => {
      state.previewMode = !!payload;
    },
    setNavSubTitle: (state, { payload }) => {
      state.navSubTitle = payload;
    },
    setNavTitle: (state, { payload }) => {
      state.navTitle = payload;
    },
    setNavVenueId: (state, { payload }) => {
      state.navVenueId = payload;
    },
    setNavVenueName: (state, { payload }) => {
      state.navVenueName = payload;
    },
    setRouteBackPath: (state, { payload }) => {
      state.backPath = payload;
    },
    setRouteBodyClasses: (state, { payload }) => {
      state.bodyClasses = payload;
    },
    setRouteEventPath: (state, { payload }) => {
      state.eventPath = payload;
    },
    setRouteHasMetadataPageTitle: (state, { payload }) => {
      state.hasMetadataPageTitle = payload;
      if (!payload) {
        state.metadataPageTitle = '';
      }
    },
    setRoutePageContext: (state, { payload }) => {
      state.pageContext = payload;
    },
    setRoutePageName: (state, { payload }) => {
      state.pageName = payload;
    },
    setRouteSearch: (state, { payload }) => {
      state.search = payload;
    },
    setRouteShowBack: (state, { payload }) => {
      state.showBack = payload;
    },
    setRouteShowLogo: (state, { payload }) => {
      state.showLogo = payload;
    },
    setRouteShowProfileMenu: (state, { payload }) => {
      state.showProfileMenu = payload;
    },
    setRouteShowNav: (state, { payload }) => {
      state.showNav = payload;
    },
    setRouteTitleKey: (state, { payload: titleKey }) => {
      state.titleKey = titleKey;
      // set metadataTitle on titleKey change
      const fallback = titleDefault;
      let metadataTitle = getTranslation({ translationKey: `titles.${titleKey}`, fallback });
      metadataTitle = metadataTitle === fallback ? fallback : `${metadataTitle} | Resy`;
      state.metadataTitle = metadataTitle;
    },
    setAffId: (state, { payload }) => {
      state.affId = payload;
    },
    setOfferId: (state, { payload }) => {
      state.offerId = payload;
    },
    setReturnUrl: (state, { payload }) => {
      state.returnUrl = payload;
    },
    setSourceId: (state, { payload }) => {
      state.sourceId = payload;
    },
    setUtmSource: (state, { payload }) => {
      state.utmSource = payload;
    },
    setPartnerId: (state, { payload }) => {
      state.partnerId = payload;
    },
    setIsBookingMetadataInited: (state, { payload }) => {
      state.isBookingMetadataInited = payload;
    },
  },
});

const {
  setLogoutRedirect, setMetadataPageTitle, setMobileFirstLogin,
  setNavSubTitle, setNavTitle, setNavVenueId, setNavVenueName, setPreviewMode,
  setRouteBackPath, setRouteBodyClasses, setRouteEventPath, setRouteHasMetadataPageTitle,
  setRoutePageContext, setRoutePageName, setRouteSearch, setRouteShowBack,
  setRouteShowLogo, setRouteShowProfileMenu, setRouteShowNav, setRouteTitleKey,
  setAffId, setOfferId, setReturnUrl, setSourceId, setUtmSource, setPartnerId, setIsBookingMetadataInited,
} = resyRouterSlice.actions;

const routerActionsList = [
  setRouteBackPath, setRouteBodyClasses, setRouteEventPath, setRouteHasMetadataPageTitle, setRoutePageContext, setRoutePageName,
  setRouteSearch, setRouteShowBack, setRouteShowLogo, setRouteShowProfileMenu, setRouteShowNav, setRouteTitleKey,
];
const routerStateKeys = Object.keys(initialRouterState);
const routerStateKeysToAction = zipObject(routerStateKeys, routerActionsList);

const getRouterStateFromMatches = (matches) => {
  const routerState = {};
  routerStateKeys.forEach((key) => {
    const filteredMatches = matches.filter((match) => match.handle?.[key] !== undefined);
    if (filteredMatches.length) {
      const lastMatch = filteredMatches.slice(-1)[0];
      routerState[key] = lastMatch.handle[key];
    }
  });
  return routerState;
};

const setRouterStateFromMatches = ({ matches, resolvedBackPath, resolvedSearch }) => (dispatch) => {
  const nextStateFromMatches = getRouterStateFromMatches(matches);
  const nextState = { ...initialRouterState, ...nextStateFromMatches };
  Object.entries(nextState).forEach(([key, value]) => {
    const action = routerStateKeysToAction[key];
    let payload = value;
    if (key === 'backPath') { payload = resolvedBackPath; }
    if (key === 'search') { payload = resolvedSearch; }
    dispatch(action(payload));
  });
};

export default resyRouterSlice.reducer;
export {
  setAffId,
  setIsBookingMetadataInited,
  setLogoutRedirect,
  setMetadataPageTitle,
  setMobileFirstLogin,
  setNavSubTitle,
  setNavTitle,
  setNavVenueId,
  setNavVenueName,
  setOfferId,
  setReturnUrl,
  setPreviewMode,
  setRouteBackPath,
  setRouteBodyClasses,
  setRouteEventPath,
  setRoutePageContext,
  setRoutePageName,
  setRouteSearch,
  setRouteShowBack,
  setRouteShowLogo,
  setRouteShowProfileMenu,
  setRouteShowNav,
  setRouteTitleKey,
  setRouterStateFromMatches,
  setSourceId,
  setUtmSource,
  setPartnerId,
};
